import { useEffect, useMemo, useState } from "react";
import { Ban, Building2, CheckCircle2, CreditCard, KeyRound, LogOut, MapPin, MessageCircle, Play, Plus, RefreshCw, Search, Send, ShieldCheck, Unlock, UserPlus, XCircle } from "lucide-react";
import { toast } from "sonner";
import MacJitLogo from "../components/MacJitLogo";
import { useAuth } from "../context/AuthContext";
import { useGarageWS } from "../hooks/useWebSocket";
import api from "../lib/api";
import { isValidEmail, isValidIndianPhone, normalizeIndianPhone } from "../lib/validation";
import { allModules, limitsForPlan, modulesForPlan, moduleOptions, planOptions, subscribedModules } from "../lib/subscriptionModules";

const emptyForm = {
  tenant_name: "",
  tenant_slug: "",
  garage_name: "",
  city: "",
  area: "",
  pincode: "",
  address: "",
  owner_name: "",
  owner_phone: "",
  owner_email: "",
  plan: "basic",
  trial_days: "14",
  branch_limit: "1",
  staff_limit: "5",
  enabled_modules: modulesForPlan("basic"),
};

const statusLabel = {
  trialing: "Trialing",
  trial_exhausted: "Trial exhausted",
  active: "Active",
  past_due: "Past due",
  expired: "Expired",
  cancelled: "Cancelled",
  suspended: "Blocked",
};

export default function PlatformAdminPage() {
  const { user, token, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [garages, setGarages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [supportConversations, setSupportConversations] = useState([]);
  const [supportLeads, setSupportLeads] = useState([]);
  const [supportIncidents, setSupportIncidents] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [created, setCreated] = useState(null);
  const [activeTab, setActiveTab] = useState("merchants");
  const [merchantFilter, setMerchantFilter] = useState("");
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [tenantRes, garageRes, subscriptionRes, planRes, checkoutRes, supportRes, leadRes, incidentRes] = await Promise.all([
        api.get("/admin/platform/tenants"),
        api.get("/admin/platform/garages"),
        api.get("/admin/platform/subscriptions"),
        api.get("/admin/platform/plans"),
        api.get("/admin/platform/checkouts"),
        api.get("/admin/platform/support/conversations"),
        api.get("/admin/platform/support/leads"),
        api.get("/admin/platform/support/incidents"),
      ]);
      setTenants(Array.isArray(tenantRes.data) ? tenantRes.data : []);
      setGarages(Array.isArray(garageRes.data) ? garageRes.data : []);
      setSubscriptions(Array.isArray(subscriptionRes.data) ? subscriptionRes.data : []);
      setPlans(Array.isArray(planRes.data) ? planRes.data : []);
      setCheckouts(Array.isArray(checkoutRes.data) ? checkoutRes.data : []);
      setSupportConversations(Array.isArray(supportRes.data) ? supportRes.data : []);
      setSupportLeads(Array.isArray(leadRes.data) ? leadRes.data : []);
      setSupportIncidents(Array.isArray(incidentRes.data) ? incidentRes.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load platform data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      api.get("/admin/platform/support/conversations")
        .then((res) => setSupportConversations(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const activeTenants = tenants.filter((t) => t.active !== false).length;
  const activeGarages = garages.filter((g) => g.active !== false).length;
  const tenantsById = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);
  const subscriptionsByTenant = useMemo(() => Object.fromEntries(subscriptions.map((s) => [s.tenant_id, s])), [subscriptions]);
  const planConfigByKey = useMemo(() => Object.fromEntries(plans.map((plan) => [plan.key, plan])), [plans]);
  const livePlanOptions = useMemo(() => {
    const source = plans.filter((plan) => plan.active !== false);
    const entries = (source.length ? source : plans).map((plan) => [plan.key, plan.name || plan.key]);
    return entries.length ? Object.fromEntries(entries) : planOptions;
  }, [plans]);
  const modulesForLivePlan = (plan) => {
    const modules = planConfigByKey[plan]?.enabled_modules;
    return Array.isArray(modules) && modules.length ? modules : modulesForPlan(plan);
  };
  const limitsForLivePlan = (plan) => {
    const config = planConfigByKey[plan];
    if (!config) return limitsForPlan(plan);
    return {
      branchLimit: Number(config.branch_limit || 1),
      staffLimit: Number(config.staff_limit || 5),
    };
  };
  const upd = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const applyPlanToForm = (plan) => {
    const limits = limitsForLivePlan(plan);
    setForm((current) => ({
      ...current,
      plan,
      branch_limit: String(limits.branchLimit),
      staff_limit: String(limits.staffLimit),
      enabled_modules: modulesForLivePlan(plan),
    }));
  };
  const toggleFormModule = (module) => setForm((f) => {
    const current = Array.isArray(f.enabled_modules) ? f.enabled_modules : allModules;
    const next = current.includes(module) ? current.filter((item) => item !== module) : [...current, module];
    return { ...f, enabled_modules: next.length ? next : current };
  });
  const merchantRows = useMemo(() => {
    const query = merchantFilter.trim().toLowerCase();
    return tenants
      .map((tenant) => {
        const subscription = subscriptionsByTenant[tenant.id] || {};
        const tenantGarages = garages.filter((garage) => garage.tenant_id === tenant.id);
        return { tenant, subscription, garages: tenantGarages };
      })
      .filter(({ tenant, subscription, garages }) => {
        if (!query) return true;
        return [tenant.name, tenant.slug, tenant.owner_email, tenant.owner_phone, subscription.plan, subscription.status, ...garages.map((garage) => `${garage.name} ${garage.city} ${garage.area}`)]
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [tenants, garages, subscriptionsByTenant, merchantFilter]);
  const filteredSubscriptions = useMemo(() => {
    const query = merchantFilter.trim().toLowerCase();
    return subscriptions.filter((subscription) => {
      if (subscriptionStatusFilter !== "all" && String(subscription.status || "").toLowerCase() !== subscriptionStatusFilter) return false;
      if (!query) return true;
      const tenant = tenantsById[subscription.tenant_id] || {};
      return [tenant.name, tenant.slug, tenant.owner_email, tenant.owner_phone, subscription.tenant_id, subscription.plan, subscription.status]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [subscriptions, tenantsById, merchantFilter, subscriptionStatusFilter]);

  useGarageWS(token, (event) => {
    if (["connected", "SUPPORT_MESSAGE", "SUPPORT_REPLY", "SUPPORT_CLOSED", "CHAT_PRESENCE"].includes(event?.type)) {
      api.get("/admin/platform/support/conversations")
        .then((res) => setSupportConversations(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tenant_name.trim()) return toast.error("Company name is required");
    if (!form.garage_name.trim()) return toast.error("Garage name is required");
    if (!form.city.trim()) return toast.error("City is required");
    if (!form.owner_name.trim()) return toast.error("Owner name is required");
    const phone = normalizeIndianPhone(form.owner_phone);
    if (!isValidIndianPhone(phone)) return toast.error("Enter a valid Indian owner mobile");
    if (!isValidEmail(form.owner_email)) return toast.error("Enter a valid owner email");

    setBusy(true);
    try {
      const res = await api.post("/admin/platform/onboard-tenant", {
        ...form,
        owner_phone: phone,
        owner_email: form.owner_email.trim().toLowerCase(),
        trial_days: Number(form.trial_days || 14),
        branch_limit: Number(form.branch_limit || 1),
        staff_limit: Number(form.staff_limit || 5),
      });
      setCreated(res.data);
      setForm(emptyForm);
      toast.success("Garage customer onboarded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not onboard tenant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-zinc-950 text-zinc-100">
      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <MacJitLogo size={34} />
            <div>
              <p className="font-display text-lg font-black tracking-tighter">MACJIT <span className="text-orange-500">/ PLATFORM</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{user?.name || "Platform admin"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/qa" className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
              QA Lab
            </a>
            <button onClick={load} disabled={loading} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-50" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={logout} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-6 py-8">
        <section className="grid gap-px bg-zinc-800 md:grid-cols-3">
          <Metric icon={Building2} label="Tenants" value={tenants.length} sub={`${activeTenants} active`} />
          <Metric icon={MapPin} label="Garages" value={garages.length} sub={`${activeGarages} active branches`} />
          <Metric icon={ShieldCheck} label="Plans" value={new Set(subscriptions.map((s) => s.plan || "basic")).size || 0} sub="configured tiers" />
        </section>

        <PlatformTabs active={activeTab} onChange={setActiveTab} />

        {(activeTab === "merchants" || activeTab === "subscriptions") && (
          <section className="border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="grid gap-3 md:grid-cols-[1fr_220px]">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Search merchant, email, phone, city, plan</span>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <input value={merchantFilter} onChange={(e) => setMerchantFilter(e.target.value)} placeholder="Search platform customers..." className="w-full border border-zinc-800 bg-black py-3 pl-10 pr-4 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500" />
                </div>
              </label>
              {activeTab === "subscriptions" && (
                <Select label="Status filter" value={subscriptionStatusFilter} onChange={setSubscriptionStatusFilter} options={{ all: "All", active: "Active", trialing: "Trialing", trial_exhausted: "Trial exhausted", suspended: "Blocked", cancelled: "Cancelled", expired: "Expired", past_due: "Past due" }} />
              )}
            </div>
          </section>
        )}

        {activeTab === "merchants" && (
          <MerchantDirectory rows={merchantRows} planOptionsMap={livePlanOptions} onOpenSubscriptions={() => setActiveTab("subscriptions")} />
        )}

        {activeTab === "subscriptions" && (
          <section className="border border-zinc-800 bg-zinc-900/40">
            <div className="border-b border-zinc-800 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Subscription control</p>
              <p className="mt-1 text-sm text-zinc-500">Cancel, block, reactivate, upgrade, and change limits without mixing merchant records.</p>
            </div>
            <div className="divide-y divide-zinc-800">
              {filteredSubscriptions.map((subscription) => (
                <SubscriptionRow key={subscription.id} subscription={subscription} tenant={tenantsById[subscription.tenant_id]} plans={plans} planOptionsMap={livePlanOptions} onChange={load} />
              ))}
              {!filteredSubscriptions.length && <p className="px-5 py-8 text-sm text-zinc-500">No subscriptions matched this filter.</p>}
            </div>
          </section>
        )}

        {activeTab === "plans" && <PlanManager plans={plans} onChange={load} />}

        {activeTab === "product-qa" && <ProductQaCenter plans={plans} subscriptions={subscriptions} tenants={tenants} />}

        {activeTab === "checkouts" && <CheckoutQueue checkouts={checkouts} onChange={load} />}

        {activeTab === "access" && <CustomerAccessReset />}

        {activeTab === "support" && <SupportInbox conversations={supportConversations} leads={supportLeads} incidents={supportIncidents} onChange={load} />}

        {activeTab === "onboarding" && (
          <section className="grid gap-6 lg:grid-cols-[420px_1fr]">
          <form onSubmit={submit} className="border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Onboard customer</p>
                <h1 className="mt-1 font-display text-2xl font-black tracking-tight">New Garage Account</h1>
              </div>
              <UserPlus className="h-5 w-5 text-orange-500" />
            </div>

            <div className="space-y-3">
              <Field label="Company name" value={form.tenant_name} onChange={(v) => upd("tenant_name", v)} required />
              <Field label="Company slug" value={form.tenant_slug} onChange={(v) => upd("tenant_slug", v)} placeholder="Optional" />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Garage name" value={form.garage_name} onChange={(v) => upd("garage_name", v)} required />
                <Field label="City" value={form.city} onChange={(v) => upd("city", v)} required />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Area" value={form.area} onChange={(v) => upd("area", v)} placeholder="Whitefield" />
                <Field label="Pincode" value={form.pincode} onChange={(v) => upd("pincode", v.replace(/\D/g, "").slice(0, 6))} placeholder="560066" />
              </div>
              <Field label="Trial days" type="number" value={form.trial_days} onChange={(v) => upd("trial_days", v)} />
              <Field label="Address" value={form.address} onChange={(v) => upd("address", v)} placeholder="Optional" />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Owner name" value={form.owner_name} onChange={(v) => upd("owner_name", v)} required />
                <Field label="Owner mobile" value={form.owner_phone} onChange={(v) => upd("owner_phone", v)} onBlur={() => form.owner_phone && upd("owner_phone", normalizeIndianPhone(form.owner_phone))} required />
              </div>
              <Field label="Owner email" type="email" value={form.owner_email} onChange={(v) => upd("owner_email", v.toLowerCase())} required />
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Plan</span>
                <select value={form.plan} onChange={(e) => applyPlanToForm(e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500">
                  {Object.entries(livePlanOptions).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Branch limit" type="number" value={form.branch_limit} onChange={(v) => upd("branch_limit", v)} />
                <Field label="Staff limit" type="number" value={form.staff_limit} onChange={(v) => upd("staff_limit", v)} />
              </div>
              <ModulePicker value={form.enabled_modules} onToggle={toggleFormModule} />
              <button disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                <Plus className="h-4 w-4" /> {busy ? "Creating..." : "Create Account"}
              </button>
            </div>
          </form>

          <div className="space-y-6">
            {created && (
              <div className="border border-emerald-500/30 bg-emerald-500/10 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="font-display text-lg font-black">Created {created.tenant?.name}</p>
                    <p className="mt-1 font-mono text-xs text-emerald-100">Owner: {created.owner?.email} · Temporary password: {created.temporary_password}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-300">Email: {created.onboarding_email?.sent ? "sent" : created.onboarding_email?.reason || "logged"}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="border border-zinc-800 bg-zinc-900/40">
              <div className="border-b border-zinc-800 px-5 py-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Recent branches</p>
              </div>
              <div className="divide-y divide-zinc-800">
                {garages.slice(0, 8).map((garage) => (
                  <div key={garage.id} className="px-5 py-4">
                    <p className="font-display text-lg font-bold">{garage.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      {tenantsById[garage.tenant_id]?.name || garage.tenant_id} · {garage.city || "City"} · {garage.area || "Area"}
                    </p>
                  </div>
                ))}
                {!garages.length && <p className="px-5 py-8 text-sm text-zinc-500">No branches yet.</p>}
              </div>
            </div>
          </div>
        </section>
        )}
      </main>
    </div>
  );
}

function PlatformTabs({ active, onChange }) {
  const tabs = [
    ["merchants", "Merchants"],
    ["subscriptions", "Subscriptions"],
    ["onboarding", "Onboarding"],
    ["checkouts", "Checkouts"],
    ["support", "Support"],
    ["product-qa", "Product QA"],
    ["plans", "Plans"],
    ["access", "Access"],
  ];
  return (
    <nav className="flex flex-wrap gap-2 border border-zinc-800 bg-zinc-900/40 p-2">
      {tabs.map(([key, label]) => (
        <button key={key} onClick={() => onChange(key)} className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition ${active === key ? "bg-orange-500 text-black" : "border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500"}`}>
          {label}
        </button>
      ))}
    </nav>
  );
}

const productSuites = {
  mini: {
    title: "Mini",
    subtitle: "Billing/POS manual regression area for the single-counter product.",
    planKey: "terminal_ledger",
    routes: [
      { label: "Open Mini", href: "/mini" },
      { label: "QA Lab", href: "/qa" },
    ],
    versions: [
      { name: "Mini", status: "Active", price: "₹99", note: "1 merchant seat, 99 free trial transactions, 10GB storage, Razorpay setup, invoice, credit sale, archive." },
      { name: "Shop Start", status: "Locked", price: "Soon", note: "Future Mini upgrade path for stronger shop workflows." },
      { name: "Parts Seller", status: "Locked", price: "Soon", note: "Future seller-focused product for public parts listing." },
    ],
    scenarios: [
      { key: "mini_trial_99", name: "99 transaction trial limit", constraints: { transaction_count: 99, storage_percent: 10 } },
      { key: "mini_storage_full", name: "10GB storage full block", constraints: { transaction_count: 10, storage_percent: 100 } },
      { key: "mini_payment_not_configured", name: "Gateway not configured", constraints: { transaction_count: 1, storage_percent: 10 } },
      { key: "mini_archive_smoke", name: "Archive/download smoke", constraints: { transaction_count: 20, storage_percent: 80 } },
      { key: "mini_worst_suite", name: "Mini worst-case suite", constraints: { transaction_count: 120, storage_percent: 120 } },
    ],
    manualChecks: [
      "Try It creates trial account and sends temp password email note.",
      "Subscribe opens Razorpay checkout and failed payment returns to MacJit status page.",
      "Create bill supports multiple products, taxes, discount, paid/credit, and invoice preview.",
      "Counter collect Razorpay and send payment link stay disabled until merchant gateway is active.",
      "Credit sale payment link updates transaction history and paid/credit totals after completion.",
      "Storage full blocks new writes and archive frees active Mini storage without deleting audit.",
    ],
  },
  terminal: {
    title: "Terminal",
    subtitle: "Garage workflow regression area for Terminal Plus and future Terminal family versions.",
    planKey: "terminal_plus",
    routes: [
      { label: "Open Terminal", href: "/terminal" },
      { label: "QA Lab", href: "/qa" },
    ],
    versions: [
      { name: "Garage Core", status: "Locked", price: "Soon", note: "Future service-only version for small workshops." },
      { name: "Garage Shop", status: "Locked", price: "Soon", note: "Future garage + inventory package." },
      { name: "Terminal Plus", status: "Active", price: "₹399", note: "50 free trial services, 99 shop transactions, 10GB storage, admin Razorpay setup, Loop and full workflow." },
    ],
    scenarios: [
      { key: "terminal_login", name: "Terminal login route", constraints: { transaction_count: 1, storage_percent: 10 } },
      { key: "plan_entitlements", name: "Mini + Terminal plan matrix", constraints: { transaction_count: 1, storage_percent: 10 } },
      { key: "support_landing", name: "Landing support handoff", constraints: { transaction_count: 1, storage_percent: 10 } },
      { key: "notification_email", name: "Notification/email readiness", constraints: { transaction_count: 1, storage_percent: 10 } },
      { key: "all_smoke", name: "Full smoke suite", constraints: { transaction_count: 120, storage_percent: 120 } },
    ],
    manualChecks: [
      "Reception 51st trial service blocks and Subscribe redirects to Terminal Plus payment.",
      "Shopkeeper 100th trial transaction blocks and Subscribe redirects to Terminal Plus payment.",
      "Admin profile shows storage usage, active plans from same email, and storage add-on purchase.",
      "Admin payment setup enables reception/shopkeeper collect/link actions after Razorpay activation.",
      "Mechanic bays auto-create for mechanics and service assignment rotates through available bays.",
      "Terminal Plus invoice uses logo only, no outer border, and normal black/white bill layout.",
      "Storage full blocks reception and shopkeeper writes, while staff only see contact-admin warning.",
      "Admin transaction archive removes active storage pressure and keeps downloadable audit history.",
    ],
  },
};

function ProductQaCenter({ plans, subscriptions, tenants }) {
  const [family, setFamily] = useState("mini");
  const [runningKey, setRunningKey] = useState("");
  const [latest, setLatest] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const suite = productSuites[family];
  const plan = plans.find((item) => item.key === suite.planKey) || {};
  const activeSubs = subscriptions.filter((sub) => sub.plan === suite.planKey && !["cancelled", "suspended", "expired", "purged"].includes(String(sub.status || "").toLowerCase()));
  const tenantNames = new Set(activeSubs.map((sub) => tenants.find((tenant) => tenant.id === sub.tenant_id)?.name).filter(Boolean));

  useEffect(() => {
    api.get("/admin/platform/readiness")
      .then((res) => setReadiness(res.data || null))
      .catch(() => setReadiness(null));
  }, []);

  const runScenario = async (scenario) => {
    setRunningKey(scenario.key);
    try {
      const res = await api.post("/qa/run", {
        scenario: scenario.key,
        environment: "local",
        debug_mode: true,
        constraints: scenario.constraints,
      });
      setLatest(res.data);
      toast.success(res.data?.passed ? `${scenario.name} passed` : `${scenario.name} completed with failures`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not run ${scenario.name}`);
    } finally {
      setRunningKey("");
    }
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-zinc-800 bg-zinc-900/40 p-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Product QA console</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">Mini and Terminal manual test desk</h2>
          <p className="mt-1 text-sm text-zinc-500">Keep product-family checks separate, reusable, and ready for local/dev/prod smoke testing.</p>
        </div>
        <div className="flex gap-2">
          {Object.entries(productSuites).map(([key, item]) => (
            <button key={key} onClick={() => { setFamily(key); setLatest(null); }} className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition ${family === key ? "bg-orange-500 text-black" : "border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500"}`}>
              {item.title}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-5">
          <div className="border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{suite.planKey}</p>
                <h3 className="mt-1 font-display text-3xl font-black">{suite.title}</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-400">{suite.subtitle}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {suite.routes.map((route) => (
                  <a key={route.href + route.label} href={route.href} className="border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
                    {route.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <MiniStat label="Configured price" value={plan.price ? `₹${plan.price}` : "Not set"} />
              <MiniStat label="Active accounts" value={activeSubs.length} />
              <MiniStat label="Customers" value={tenantNames.size} />
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/40">
            <div className="border-b border-zinc-800 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Versions</p>
              <p className="mt-1 text-sm text-zinc-500">Use this to remember what belongs to this product family while only active backend plans stay live.</p>
            </div>
            <div className="divide-y divide-zinc-800">
              {suite.versions.map((version) => (
                <div key={version.name} className="grid gap-3 px-5 py-4 md:grid-cols-[1fr_auto] md:items-center">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-lg font-bold">{version.name}</p>
                      <span className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${version.status === "Active" ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-500"}`}>
                        {version.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-zinc-500">{version.note}</p>
                  </div>
                  <p className="font-display text-xl font-black text-orange-400">{version.price}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/40">
            <div className="border-b border-zinc-800 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Manual checklist</p>
              <p className="mt-1 text-sm text-zinc-500">The exact flows we already tested and should recheck before production pushes.</p>
            </div>
            <div className="grid gap-px bg-zinc-800 sm:grid-cols-2">
              {suite.manualChecks.map((check) => (
                <div key={check} className="flex gap-3 bg-zinc-950 p-4">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-orange-500" />
                  <p className="text-sm leading-relaxed text-zinc-300">{check}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="border border-zinc-800 bg-zinc-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 px-5 py-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Production readiness</p>
                <p className="mt-1 text-sm text-zinc-500">{readiness?.summary || "Loading Mini production checks..."}</p>
              </div>
              {readiness && (
                <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${readiness.ready ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
                  {readiness.ready ? "Ready" : `${readiness.failed_count || 0} issues`}
                </span>
              )}
            </div>
            <div className="divide-y divide-zinc-800">
              {(readiness?.checks || []).map((check) => (
                <div key={check.name} className="flex gap-3 px-5 py-3">
                  {check.passed ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-400" /> : <XCircle className="mt-0.5 h-4 w-4 flex-none text-red-400" />}
                  <div>
                    <p className="font-display font-semibold">{check.name}</p>
                    <p className="mt-1 text-xs leading-relaxed text-zinc-500">{check.message}</p>
                  </div>
                </div>
              ))}
              {!readiness && <p className="px-5 py-5 text-sm text-zinc-500">Readiness checks are available for platform owner accounts.</p>}
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/40">
            <div className="border-b border-zinc-800 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Quick QA runs</p>
              <p className="mt-1 text-sm text-zinc-500">Runs the same QA Lab backend with debug mode and prefilled constraints.</p>
            </div>
            <div className="divide-y divide-zinc-800">
              {suite.scenarios.map((scenario) => (
                <div key={scenario.key} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <p className="font-display font-bold">{scenario.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{scenario.key}</p>
                  </div>
                  <button onClick={() => runScenario(scenario)} disabled={!!runningKey} className="inline-flex items-center gap-2 border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                    <Play className="h-3.5 w-3.5" /> {runningKey === scenario.key ? "Running" : "Run"}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-zinc-800 bg-black p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Latest result</p>
            {latest ? (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-xl font-black">{latest.scenario}</p>
                  <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${latest.passed ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
                    {latest.passed ? "Passed" : "Failed"}
                  </span>
                </div>
                <div className="max-h-80 overflow-auto border border-zinc-800">
                  {(latest.steps || []).map((step, index) => (
                    <div key={`${step.name}-${index}`} className="border-b border-zinc-800 p-3 last:border-b-0">
                      <p className={`font-display font-semibold ${step.passed ? "text-emerald-300" : "text-red-300"}`}>{step.name}</p>
                      <p className="mt-1 text-xs leading-relaxed text-zinc-500">{step.detail || step.description || "No detail captured."}</p>
                      {step.source && <p className="mt-2 break-all font-mono text-[10px] uppercase tracking-[0.12em] text-orange-300">{step.source.file}:{step.source.line} · {step.source.component}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">Run any scenario from this product tab to see pass/fail, source file, line, and component details here.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-zinc-800 bg-black p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-black">{value}</p>
    </div>
  );
}

function MerchantDirectory({ rows, planOptionsMap, onOpenSubscriptions }) {
  return (
    <section className="border border-zinc-800 bg-zinc-900/40">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 px-5 py-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Merchant directory</p>
          <p className="mt-1 text-sm text-zinc-500">One row per platform customer with branches and subscription snapshot.</p>
        </div>
        <button onClick={onOpenSubscriptions} className="border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
          Manage subscriptions
        </button>
      </div>
      <div className="divide-y divide-zinc-800">
        {rows.map(({ tenant, subscription, garages }) => {
          const access = subscription.access_allowed !== false && tenant.active !== false;
          return (
            <div key={tenant.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[1.2fr_1fr_auto] lg:items-center">
              <div className="min-w-0">
                <p className="font-display text-lg font-bold">{tenant.name}</p>
                <p className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  {tenant.slug} · {tenant.owner_email || "No email"} · {tenant.owner_phone || "No phone"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">
                  {planOptionsMap[subscription.plan] || subscription.plan || tenant.plan || "Plan pending"} · {statusLabel[subscription.status] || subscription.status || tenant.status || "Active"}
                </p>
                <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.14em] text-orange-300">
                  {garages.map((garage) => firstText(garage.city, garage.area, garage.name)).filter(Boolean).join(" · ") || "No branches"}
                </p>
              </div>
              <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
                <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${access ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
                  {access ? "Access on" : "Blocked"}
                </span>
                <span className="border border-zinc-700 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                  {garages.length} branches
                </span>
              </div>
            </div>
          );
        })}
        {!rows.length && <p className="px-5 py-8 text-sm text-zinc-500">No merchants matched this filter.</p>}
      </div>
    </section>
  );
}

function PlanManager({ plans, onChange }) {
  const [drafts, setDrafts] = useState({});
  const [openKey, setOpenKey] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newPlan, setNewPlan] = useState({
    key: "",
    name: "",
    description: "",
    price: 0,
    currency: "INR",
    trial_days: 14,
    branch_limit: 1,
    staff_limit: 2,
    product_limit: 0,
    invoice_limit: 0,
    enabled_modules: ["shop", "inventory", "loop", "payments"],
    active: true,
  });
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    setDrafts(Object.fromEntries(plans.map((plan) => [plan.key, normalizePlanDraft(plan)])));
  }, [plans]);

  const updateDraft = (key, field, value) => {
    setDrafts((current) => ({ ...current, [key]: { ...current[key], [field]: value } }));
  };

  const toggleModule = (setter, module) => {
    setter((current) => {
      const enabled = Array.isArray(current.enabled_modules) ? current.enabled_modules : allModules;
      const next = enabled.includes(module) ? enabled.filter((item) => item !== module) : [...enabled, module];
      return { ...current, enabled_modules: next.length ? next : enabled };
    });
  };

  const toggleDraftModule = (key, module) => {
    setDrafts((current) => {
      const draft = current[key] || {};
      const enabled = Array.isArray(draft.enabled_modules) ? draft.enabled_modules : allModules;
      const next = enabled.includes(module) ? enabled.filter((item) => item !== module) : [...enabled, module];
      return { ...current, [key]: { ...draft, enabled_modules: next.length ? next : enabled } };
    });
  };

  const savePlan = async (key) => {
    const draft = drafts[key];
    if (!draft?.name?.trim()) return toast.error("Plan name is required");
    setSavingKey(key);
    try {
      await api.patch(`/admin/platform/plans/${key}`, planPayload(draft));
      toast.success("Plan updated");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save plan");
    } finally {
      setSavingKey("");
    }
  };

  const createPlan = async () => {
    if (!newPlan.key.trim() || !newPlan.name.trim()) return toast.error("Plan key and name are required");
    setSavingKey("new");
    try {
      await api.post("/admin/platform/plans", planPayload(newPlan));
      toast.success("Plan created");
      setNewPlan({
        key: "",
        name: "",
        description: "",
        price: 0,
        currency: "INR",
        trial_days: 14,
        branch_limit: 1,
        staff_limit: 2,
        product_limit: 0,
        invoice_limit: 0,
        enabled_modules: ["shop", "inventory", "loop", "payments"],
        active: true,
      });
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create plan");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <section className="border border-zinc-800 bg-zinc-900/40">
      <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Plan configuration</p>
          <p className="mt-1 text-sm text-zinc-500">Modules, staff limits and Loop eligibility are now controlled from here.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-orange-500 px-4 py-3 font-display text-xs font-black uppercase tracking-widest text-black hover:bg-orange-400">
          <Plus className="h-4 w-4" /> Create Plan
        </button>
      </div>
      <div className="divide-y divide-zinc-800">
        {plans.map((plan) => {
          const draft = drafts[plan.key] || normalizePlanDraft(plan);
          const isOpen = openKey === plan.key;
          return (
            <div key={plan.key} className="px-5 py-4">
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? "" : plan.key)}
                className={`flex w-full items-center justify-between gap-4 border px-4 py-4 text-left transition ${isOpen ? "border-orange-500 bg-orange-500/10" : "border-zinc-800 bg-zinc-950 hover:border-orange-500/60"}`}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-lg font-black">{draft.name || plan.key}</p>
                    <span className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${draft.active !== false ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-500"}`}>
                      {draft.active !== false ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                    {plan.key} · {draft.branch_limit || 1} branches · {draft.staff_limit || 1} staff · {(draft.enabled_modules || []).join(" / ")}
                  </p>
                </div>
                <span className="shrink-0 font-mono text-2xl text-orange-500">{isOpen ? "-" : "+"}</span>
              </button>
              {isOpen && (
                <div className="space-y-4 border-x border-b border-zinc-800 bg-zinc-950/40 px-4 py-4">
                  <div className="grid gap-3 md:grid-cols-[1fr_130px_110px_110px_auto] md:items-end">
                    <Field label="Plan name" value={draft.name} onChange={(v) => updateDraft(plan.key, "name", v)} />
                    <Field label="Price" type="number" value={draft.price} onChange={(v) => updateDraft(plan.key, "price", v)} />
                    <Field label="Branches" type="number" value={draft.branch_limit} onChange={(v) => updateDraft(plan.key, "branch_limit", v)} />
                    <Field label="Staff" type="number" value={draft.staff_limit} onChange={(v) => updateDraft(plan.key, "staff_limit", v)} />
                    <label className="flex items-center gap-2 border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                      <input type="checkbox" checked={draft.active !== false} onChange={(e) => updateDraft(plan.key, "active", e.target.checked)} />
                      Active
                    </label>
                  </div>
                  <Field label="Description" value={draft.description} onChange={(v) => updateDraft(plan.key, "description", v)} placeholder="Short sales/admin note" />
                  <div className="grid gap-3 md:grid-cols-3">
                    <Field label="Trial days" type="number" value={draft.trial_days} onChange={(v) => updateDraft(plan.key, "trial_days", v)} />
                    <Field label="Product limit" type="number" value={draft.product_limit} onChange={(v) => updateDraft(plan.key, "product_limit", v)} />
                    <Field label="Invoice limit" type="number" value={draft.invoice_limit} onChange={(v) => updateDraft(plan.key, "invoice_limit", v)} />
                  </div>
                  <ModulePicker value={draft.enabled_modules} onToggle={(module) => toggleDraftModule(plan.key, module)} compact />
                  <button onClick={() => savePlan(plan.key)} disabled={savingKey === plan.key} className="inline-flex items-center gap-2 border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                    <CreditCard className="h-3.5 w-3.5" /> {savingKey === plan.key ? "Saving..." : `Save ${draft.name || plan.key}`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-orange-500/50 bg-zinc-950 shadow-2xl shadow-orange-950/30">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-5 py-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Create custom plan</p>
                <p className="mt-1 text-sm text-zinc-500">Build a new sellable bundle without touching existing plans.</p>
              </div>
              <button onClick={() => setShowCreate(false)} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Close create plan">
                <span className="block font-mono text-xl leading-none">x</span>
              </button>
            </div>
            <div className="space-y-4 px-5 py-5">
              <div className="grid gap-3 md:grid-cols-4">
                <Field label="Plan key" value={newPlan.key} onChange={(v) => setNewPlan((current) => ({ ...current, key: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))} placeholder="shop_plus" />
                <Field label="Plan name" value={newPlan.name} onChange={(v) => setNewPlan((current) => ({ ...current, name: v }))} placeholder="Shop Plus" />
                <Field label="Branches" type="number" value={newPlan.branch_limit} onChange={(v) => setNewPlan((current) => ({ ...current, branch_limit: v }))} />
                <Field label="Staff" type="number" value={newPlan.staff_limit} onChange={(v) => setNewPlan((current) => ({ ...current, staff_limit: v }))} />
              </div>
              <div className="grid gap-3 md:grid-cols-4">
                <Field label="Price" type="number" value={newPlan.price} onChange={(v) => setNewPlan((current) => ({ ...current, price: v }))} />
                <Field label="Trial days" type="number" value={newPlan.trial_days} onChange={(v) => setNewPlan((current) => ({ ...current, trial_days: v }))} />
                <Field label="Product limit" type="number" value={newPlan.product_limit} onChange={(v) => setNewPlan((current) => ({ ...current, product_limit: v }))} />
                <Field label="Invoice limit" type="number" value={newPlan.invoice_limit} onChange={(v) => setNewPlan((current) => ({ ...current, invoice_limit: v }))} />
              </div>
              <Field label="Description" value={newPlan.description} onChange={(v) => setNewPlan((current) => ({ ...current, description: v }))} placeholder="Custom bundle for seller-only customers" />
              <ModulePicker value={newPlan.enabled_modules} onToggle={(module) => toggleModule(setNewPlan, module)} compact />
              <div className="flex flex-wrap gap-3">
                <button onClick={createPlan} disabled={savingKey === "new"} className="inline-flex items-center gap-2 bg-orange-500 px-4 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                  <Plus className="h-4 w-4" /> {savingKey === "new" ? "Creating..." : "Create Plan"}
                </button>
                <button onClick={() => setShowCreate(false)} className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 font-display font-black uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function CheckoutQueue({ checkouts, onChange }) {
  const [busyId, setBusyId] = useState("");
  const recent = (checkouts || []).slice(0, 8);
  const retry = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/platform/checkouts/${id}/retry-payment`);
      toast.success("Payment link regenerated");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not retry payment");
    } finally {
      setBusyId("");
    }
  };
  const activate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/platform/checkouts/${id}/activate`);
      toast.success("Checkout activated");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not activate checkout");
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="border border-zinc-800 bg-zinc-900/40">
      <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Checkout delivery</p>
          <p className="mt-1 text-sm text-zinc-500">Buy Now payments, onboarding status, and manual recovery controls.</p>
        </div>
        <CreditCard className="h-5 w-5 text-orange-500" />
      </div>
      <div className="divide-y divide-zinc-800">
        {recent.map((checkout) => {
          const active = String(checkout.status || "").toLowerCase() === "active";
          return (
            <div key={checkout.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="min-w-0">
                <p className="font-display text-lg font-bold">{checkout.tenant_name || "Terminal customer"}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  {checkout.plan_name || checkout.plan} · ₹{checkout.amount || 0} · {checkout.owner_email || "No email"} · {checkout.status || "pending"}
                </p>
                <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.14em] text-orange-300">
                  {checkout.id} {checkout.payment_url ? `· ${checkout.payment_url}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {checkout.payment_url && (
                  <a href={checkout.payment_url} target="_blank" rel="noreferrer" className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                    Payment
                  </a>
                )}
                <button onClick={() => retry(checkout.id)} disabled={busyId === checkout.id || active} className="border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-40">
                  Retry Link
                </button>
                <button onClick={() => activate(checkout.id)} disabled={busyId === checkout.id || active} className="bg-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-40">
                  Activate
                </button>
              </div>
            </div>
          );
        })}
        {!recent.length && <p className="px-5 py-8 text-sm text-zinc-500">No checkout requests yet.</p>}
      </div>
    </section>
  );
}

function CustomerAccessReset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!isValidEmail(normalized)) return toast.error("Enter a valid registered customer email");
    setBusy(true);
    try {
      const res = await api.post("/auth/platform/reset-password", { email: normalized });
      setResult(res.data || {});
      toast.success("Temporary password and reset link sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset customer access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="grid gap-5 lg:grid-cols-[1fr_420px] lg:items-start">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Customer access recovery</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">Platform Assisted Password Reset</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-400">
            Find a tenant, Mini customer, or staff account by registered email. We send a temporary password and secure reset link to the same mailbox.
          </p>
          {result && (
            <div className="mt-4 border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="font-display text-lg font-black text-emerald-100">{result.name || result.email}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-300">
                {result.role} · {result.reset_email?.sent ? "email sent" : result.reset_email?.reason || "email logged"}
              </p>
              {result.temporary_password && (
                <p className="mt-2 font-mono text-xs text-emerald-100">Temporary password: {result.temporary_password}</p>
              )}
            </div>
          )}
        </div>
        <form onSubmit={submit} className="border border-zinc-800 bg-zinc-950 p-4">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Registered email</span>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} required placeholder="owner@example.com" className="w-full border border-zinc-800 bg-black py-3 pl-10 pr-4 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
            </div>
          </label>
          <button disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            <KeyRound className="h-4 w-4" /> {busy ? "Sending..." : "Send reset access"}
          </button>
        </form>
      </div>
    </section>
  );
}

function SupportInbox({ conversations, leads, incidents, onChange }) {
  const [replyById, setReplyById] = useState({});
  const [busyId, setBusyId] = useState("");
  const [tab, setTab] = useState("chats");
  const openItems = conversations
    .filter((item) => item.status !== "CLOSED")
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  const items = openItems.length ? openItems : conversations.slice(0, 5);

  const reply = async (id) => {
    const message = (replyById[id] || "").trim();
    if (!message) return toast.error("Type a reply first");
    setBusyId(id);
    try {
      await api.post(`/admin/platform/support/conversations/${id}/messages`, { message });
      setReplyById((current) => ({ ...current, [id]: "" }));
      toast.success("Reply sent");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reply");
    } finally {
      setBusyId("");
    }
  };

  const closeChat = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/platform/support/conversations/${id}/close`);
      toast.success("Chat ended and saved to future offers");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not end chat");
    } finally {
      setBusyId("");
    }
  };

  const createIncident = async (conversation) => {
    setBusyId(conversation.id);
    try {
      const latestVisitor = [...(conversation.messages || [])].reverse().find((message) => message.from !== "support");
      await api.post(`/admin/platform/support/conversations/${conversation.id}/incident`, {
        title: `Product support - ${conversation.support_ref}`,
        summary: latestVisitor?.text || "Customer needs help with running product",
        priority: "NORMAL",
      });
      toast.success("Incident created for development team");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create incident");
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="border border-zinc-800 bg-zinc-900/40">
      <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Product support</p>
          <p className="mt-1 text-sm text-zinc-500">Live chats from the public product landing page</p>
        </div>
        <MessageCircle className="h-5 w-5 text-orange-500" />
      </div>
      <div className="flex border-b border-zinc-800">
        <button onClick={() => setTab("chats")} className={`px-5 py-3 font-mono text-[10px] uppercase tracking-widest ${tab === "chats" ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-orange-500"}`}>Live chats</button>
        <button onClick={() => setTab("incidents")} className={`px-5 py-3 font-mono text-[10px] uppercase tracking-widest ${tab === "incidents" ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-orange-500"}`}>Product incidents</button>
        <button onClick={() => setTab("leads")} className={`px-5 py-3 font-mono text-[10px] uppercase tracking-widest ${tab === "leads" ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-orange-500"}`}>Future offers</button>
      </div>
      <div className="divide-y divide-zinc-800">
        {tab === "chats" && items.map((conversation, index) => (
          <div key={conversation.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_360px]">
            <div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-lg font-bold">{conversation.visitor_name}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className="border border-orange-500/40 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-orange-300">FIFO #{index + 1}</span>
                    {conversation.incident_id && <span className="border border-cyan-500/40 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-cyan-300">Incident created</span>}
                  </div>
                </div>
                <span className={`shrink-0 border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${conversation.support_online ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-400"}`}>
                  {conversation.support_online ? "Owner online" : "Offline fallback"}
                </span>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                {conversation.support_ref} · {conversation.visitor_email} · {conversation.visitor_phone || "No phone"} · {conversation.status} · reused for 7 days
              </p>
              <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
                {(conversation.messages || []).map((message, index) => (
                  <div key={`${message.ts}-${index}`} className={`border px-3 py-2 ${message.from === "support" ? "border-orange-500/30 bg-orange-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{message.from === "support" ? "You" : conversation.visitor_name}</p>
                    <p className="mt-1 text-sm text-zinc-200">{message.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="self-end">
              <textarea
                value={replyById[conversation.id] || ""}
                onChange={(e) => setReplyById((current) => ({ ...current, [conversation.id]: e.target.value }))}
                rows={4}
                placeholder="Reply as product owner..."
                className="w-full resize-none border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500"
              />
              <button onClick={() => reply(conversation.id)} disabled={busyId === conversation.id} className="mt-2 flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                <Send className="h-4 w-4" /> {busyId === conversation.id ? "Sending..." : "Reply"}
              </button>
              <button onClick={() => createIncident(conversation)} disabled={busyId === conversation.id || !!conversation.incident_id} className="mt-2 flex w-full items-center justify-center gap-2 border border-cyan-500/50 py-3 font-display font-black uppercase tracking-widest text-cyan-300 hover:bg-cyan-500 hover:text-black disabled:opacity-40">
                Create Incident
              </button>
              <button onClick={() => closeChat(conversation.id)} disabled={busyId === conversation.id || conversation.status === "CLOSED"} className="mt-2 flex w-full items-center justify-center gap-2 border border-zinc-700 py-3 font-display font-black uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-40">
                End Chat
              </button>
            </div>
          </div>
        ))}
        {tab === "chats" && !items.length && <p className="px-5 py-8 text-sm text-zinc-500">No support chats yet.</p>}
        {tab === "incidents" && incidents.map((incident) => (
          <div key={incident.id} className="grid gap-2 px-5 py-4 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="font-display text-lg font-bold">{incident.title || "Product incident"}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                {incident.support_ref} · {incident.customer_email || "No email"} · {incident.customer_phone || "No phone"} · {incident.status}
              </p>
              <p className="mt-1 text-sm text-zinc-400">{incident.summary || "No summary stored"}</p>
            </div>
            <span className="border border-cyan-500/40 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300">{incident.priority || "Normal"}</span>
          </div>
        ))}
        {tab === "incidents" && !incidents.length && <p className="px-5 py-8 text-sm text-zinc-500">No product incidents yet. Create one from a customer support chat.</p>}
        {tab === "leads" && leads.map((lead) => (
          <div key={lead.id} className="grid gap-2 px-5 py-4 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="font-display text-lg font-bold">{lead.name || "Lead"}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                {lead.latest_support_ref} · {lead.email || "No email"} · {lead.phone || "No phone"} · {lead.conversation_count || 1} chats
              </p>
              <p className="mt-1 text-sm text-zinc-400">{lead.last_message || "No message stored"}</p>
            </div>
            <span className="border border-orange-500/40 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-orange-300">Future offer</span>
          </div>
        ))}
        {tab === "leads" && !leads.length && <p className="px-5 py-8 text-sm text-zinc-500">No future-offer leads yet. End a chat to save one.</p>}
      </div>
    </section>
  );
}

function SubscriptionRow({ subscription, tenant, plans, planOptionsMap, onChange }) {
  const planConfigByKey = useMemo(() => Object.fromEntries((plans || []).map((plan) => [plan.key, plan])), [plans]);
  const modulesForLivePlan = (plan) => {
    const modules = planConfigByKey[plan]?.enabled_modules;
    return Array.isArray(modules) && modules.length ? modules : modulesForPlan(plan);
  };
  const limitsForLivePlan = (plan) => {
    const config = planConfigByKey[plan];
    if (!config) return limitsForPlan(plan);
    return { branchLimit: Number(config.branch_limit || 1), staffLimit: Number(config.staff_limit || 5) };
  };
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    plan: subscription.plan || "basic",
    status: subscription.status || "trialing",
    trial_ends_at: dateInput(subscription.trial_ends_at),
    current_period_ends_at: dateInput(subscription.current_period_ends_at),
    grace_ends_at: dateInput(subscription.grace_ends_at),
    branch_limit: subscription.branch_limit || 1,
    staff_limit: subscription.staff_limit || 5,
    enabled_modules: subscribedModules(subscription),
  });

  const applyPlan = (plan) => {
    const limits = limitsForLivePlan(plan);
    setForm((current) => ({
      ...current,
      plan,
      branch_limit: limits.branchLimit,
      staff_limit: limits.staffLimit,
      enabled_modules: modulesForLivePlan(plan),
    }));
  };

  const update = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/platform/subscriptions/${subscription.id}`, {
        ...form,
        trial_ends_at: toIso(form.trial_ends_at),
        current_period_ends_at: toIso(form.current_period_ends_at),
        grace_ends_at: toIso(form.grace_ends_at),
        branch_limit: Number(form.branch_limit || 1),
        staff_limit: Number(form.staff_limit || 5),
        enabled_modules: form.enabled_modules,
        active: !["expired", "cancelled", "suspended"].includes(form.status),
      });
      toast.success("Subscription updated");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update subscription");
    } finally {
      setSaving(false);
    }
  };
  const control = async (action) => {
    const reason = action === "reactivate" ? "" : window.prompt(`${action === "block" ? "Block" : "Cancel"} reason`, "");
    if (reason === null) return;
    setSaving(true);
    try {
      await api.post(`/admin/platform/subscriptions/${subscription.id}/${action}`, reason ? { reason } : {});
      toast.success(action === "reactivate" ? "Subscription reactivated" : action === "block" ? "Subscription blocked" : "Subscription cancelled");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action} subscription`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg font-bold">{tenant?.name || subscription.tenant_id}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            {statusLabel[subscription.status] || subscription.status} · {subscription.access_allowed ? "Access on" : subscription.reason || "Access blocked"}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">
            Branches {subscription.branch_usage || 0}/{subscription.branch_limit || "-"} · Staff {subscription.staff_usage || 0}/{subscription.staff_limit || "-"}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-orange-300">
            {subscribedModules(subscription).join(" · ")}
            {subscription.entitlements?.loop_eligible && !subscription.entitlements?.loop_enabled ? " · Loop pending staff 2+" : ""}
          </p>
        </div>
        <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${subscription.access_allowed ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
          {subscription.days_remaining >= 0 ? `${subscription.days_remaining} days` : "No end date"}
        </span>
      </div>
      {(subscription.branch_limit_reached || subscription.staff_limit_reached) && (
        <div className="border border-orange-500/30 bg-orange-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-orange-100">
          {subscription.branch_limit_reached && "Branch limit reached"}
          {subscription.branch_limit_reached && subscription.staff_limit_reached && " · "}
          {subscription.staff_limit_reached && "Staff limit reached"}
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-3">
        <Select label="Plan" value={form.plan} onChange={applyPlan} options={planOptionsMap || planOptions} />
        <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={statusLabel} />
        <Field label="Trial ends" type="date" value={form.trial_ends_at} onChange={(v) => setForm({ ...form, trial_ends_at: v })} />
        <Field label="Period ends" type="date" value={form.current_period_ends_at} onChange={(v) => setForm({ ...form, current_period_ends_at: v })} />
        <Field label="Grace ends" type="date" value={form.grace_ends_at} onChange={(v) => setForm({ ...form, grace_ends_at: v })} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Branches" type="number" value={form.branch_limit} onChange={(v) => setForm({ ...form, branch_limit: v })} />
          <Field label="Staff" type="number" value={form.staff_limit} onChange={(v) => setForm({ ...form, staff_limit: v })} />
        </div>
      </div>
      <ModulePicker value={form.enabled_modules} onToggle={(module) => {
        setForm((current) => {
          const enabled = Array.isArray(current.enabled_modules) ? current.enabled_modules : allModules;
          const next = enabled.includes(module) ? enabled.filter((item) => item !== module) : [...enabled, module];
          return { ...current, enabled_modules: next.length ? next : enabled };
        });
      }} compact />
      <div className="flex flex-wrap gap-2">
        <button onClick={update} disabled={saving} className="inline-flex items-center gap-2 border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
          <CreditCard className="h-3.5 w-3.5" /> {saving ? "Saving..." : "Update Subscription"}
        </button>
        <button onClick={() => control("block")} disabled={saving || form.status === "suspended"} className="inline-flex items-center gap-2 border border-yellow-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-yellow-300 hover:bg-yellow-500 hover:text-black disabled:opacity-40">
          <Ban className="h-3.5 w-3.5" /> Block
        </button>
        <button onClick={() => control("cancel")} disabled={saving || form.status === "cancelled"} className="inline-flex items-center gap-2 border border-red-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-red-300 hover:bg-red-500 hover:text-black disabled:opacity-40">
          <XCircle className="h-3.5 w-3.5" /> Cancel
        </button>
        <button onClick={() => control("reactivate")} disabled={saving || form.status === "active"} className="inline-flex items-center gap-2 border border-emerald-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300 hover:bg-emerald-500 hover:text-black disabled:opacity-40">
          <Unlock className="h-3.5 w-3.5" /> Reactivate
        </button>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-zinc-950 p-5">
      <Icon className="mb-4 h-5 w-5 text-orange-500" />
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-4xl font-black">{value}</p>
      <p className="mt-1 text-sm text-zinc-500">{sub}</p>
    </div>
  );
}

function ModulePicker({ value, onToggle, compact = false }) {
  const selected = Array.isArray(value) && value.length ? value : allModules;
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Enabled modules</p>
      <div className={`mt-2 grid gap-2 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {moduleOptions.map((module) => (
          <button
            type="button"
            key={module.key}
            onClick={() => onToggle(module.key)}
            className={`border px-3 py-2 text-left font-mono text-[10px] uppercase tracking-widest ${selected.includes(module.key) ? "border-orange-500 bg-orange-500/10 text-orange-200" : "border-zinc-800 bg-zinc-950 text-zinc-500"}`}
          >
            {module.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, onBlur, type = "text", placeholder = "", required = false }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder || label}
        className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500"
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500">
        {Object.entries(options).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
    </label>
  );
}

function normalizePlanDraft(plan = {}) {
  return {
    key: plan.key || "",
    name: plan.name || plan.key || "",
    description: plan.description || "",
    price: plan.price ?? 0,
    currency: plan.currency || "INR",
    trial_days: plan.trial_days ?? 14,
    branch_limit: plan.branch_limit ?? 1,
    staff_limit: plan.staff_limit ?? 5,
    product_limit: plan.product_limit ?? 0,
    invoice_limit: plan.invoice_limit ?? 0,
    enabled_modules: Array.isArray(plan.enabled_modules) && plan.enabled_modules.length ? plan.enabled_modules : allModules,
    active: plan.active !== false,
  };
}

function planPayload(plan) {
  return {
    ...plan,
    price: Number(plan.price || 0),
    trial_days: Number(plan.trial_days || 0),
    branch_limit: Number(plan.branch_limit || 1),
    staff_limit: Number(plan.staff_limit || 1),
    product_limit: Number(plan.product_limit || 0),
    invoice_limit: Number(plan.invoice_limit || 0),
    enabled_modules: Array.isArray(plan.enabled_modules) ? plan.enabled_modules : allModules,
    active: plan.active !== false,
  };
}

function dateInput(value) {
  return value ? String(value).slice(0, 10) : "";
}

function toIso(value) {
  return value ? `${value}T23:59:59Z` : "";
}

function firstText(...values) {
  return values.map((value) => String(value || "").trim()).find(Boolean) || "";
}
